// Claremont McKenna College: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - CMC's Common Data Set 2025-2026 (CDS_2025-26.pdf from cmc.edu): C1
//    applicants/admits/enrollees for Fall 2025 with the residency table; C8
//    test policy; C21 early decision (Early Decision I and II reported
//    together). Quirks: page footers still read "Common Data Set 2024-2025"
//    while the cover, C1 (Fall 2025) and G1 (2026-2027) are 2025-2026; the
//    C8 page cuts off the term its question names; section G is left blank,
//    so costs come from CMC Financial Aid's 2026-2027 cost of attendance.
//  - cmc.edu: First-Year Application Instructions (deadlines, early decision
//    terms, test-optional policy) and the Test-Optional Admission Policy FAQ.
//    CMC is test-optional through the Fall 2027 entry term and returns to
//    requiring the SAT or ACT for Fall 2028 (high school class of 2028).
//    The deadline list carries no year; it is the page as posted alongside
//    the Fall 2027 test policy.
//  - College Scorecard, school id 112260 (net price by family income). The
//    DEMO_KEY API was over its rate limit on 2026-10-01, so the figures were
//    read from the Scorecard's own school page, which shows the same latest
//    year the API serves.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'CMC Common Data Set 2025-2026',
        url: 'https://www.cmc.edu/sites/default/files/CDS_2025-26.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'CMC Admission, First-Year Application Instructions',
        url: 'https://www.cmc.edu/admission/first-year-application-instructions',
        read: '2026-10-01',
    },
    testFaq: {
        label: 'CMC Admission, Test-Optional Admission Policy FAQ',
        url: 'https://www.cmc.edu/admission/first-year-application-instructions/test-optional-policy-faq',
        read: '2026-10-01',
    },
    costs: {
        label: 'CMC Financial Aid, College Costs and Payments',
        url: 'https://www.cmc.edu/financial-aid/college-costs-and-payments',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?112260-Claremont-McKenna-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'claremont-mckenna-college',
    kbId: 'claremont_mckenna_college',
    name: 'Claremont McKenna College',
    shortName: 'CMC',
    aliases: ['Claremont McKenna'],
    location: 'Claremont, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (CMC colors); decoration only.
    branding: {
        primary: '#60001E',
        secondary: '#DEB02E',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/claremont_mckenna_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 6939,
        admitted: 655,
        enrolled: 335,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2599, admitted: 267 },
            { group: 'Out-of-state', applied: 1996, admitted: 273 },
            { group: 'International', applied: 2344, admitted: 115 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'CMC\'s Common Data Set reports one first-year acceptance rate for the college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together for fall 2025. CMC has no early action plan.',
        rows: [
            { plan: 'Early Decision I and II', applied: 978, admitted: 216 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 10' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'By December 15', binding: true },
            { plan: 'Early Decision II', deadline: 'January 10', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 10', decision: 'By April 1', binding: false },
        ],
        note: 'Admitted students reply in early January (Early Decision I), late February (Early Decision II) or by May 1 (Regular Decision). For priority merit scholarship consideration, apply by December 1 with all supporting documents in by January 10.',
        source: 'firstYear',
        binding: 'Early decision is binding: if you are admitted, you are expected to enroll at CMC, withdraw all your other applications and not start new ones. You, a parent and your college counselor sign the Early Decision Agreement. If CMC is not clearly your first choice, CMC asks you to apply Regular Decision.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'CMC\'s test-optional policy runs through the Fall 2027 entry term. You choose whether to send SAT or ACT scores; if you do not, you are not disadvantaged in admission or merit scholarship review. If you send scores, CMC superscores within the SAT or the ACT, and you may report scores yourself on the application.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'CMC returns to requiring the SAT or ACT for the Fall 2028 admission cycle (the high school class of 2028).', source: 'testFaq' },
        ],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 75300 },
            { item: 'Student activity fee (ASCMC)', amount: 420 },
            { item: 'Housing (single or double)', amount: 12490 },
            { item: 'Food (19 meals a week)', amount: 10780 },
            { item: 'Books, transportation and miscellaneous (estimated)', amount: 3000 },
        ],
        total: 101990,
        note: 'First-year students also pay a one-time $500 orientation fee. Students without their own health insurance are enrolled in the Claremont Colleges plan, $3,312 for the year.',
        source: 'costs',
        netPrice: {
            average: 26039,
            byIncome: [
                { income: '$0 to $30,000', amount: 5423 },
                { income: '$30,001 to $48,000', amount: 4205 },
                { income: '$48,001 to $75,000', amount: 19127 },
                { income: '$75,001 to $110,000', amount: 31487 },
                { income: 'Over $110,000', amount: 52425 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from CMC\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
